import { filter, map, Observable, pairwise, switchMap } from 'rxjs';
import { CrudManagerService } from '../crud-manager';
import { CrudTemplate } from '../crud-templates.interface';
import { CrudManagerUtils } from '../crud-manager/crud-manager.utils';
import { DEFAULT_EDITOR_DEFINITION_ID, IdItemModel, ItemEditMode, ItemModelActionData } from '@core/shared/model';
import { CRUD_ROUTE_STATE_ITEM_DEFAULTS, CrudFormServiceFactory, makeFormServiceSaveHandlerApiCrudBatchCreate } from '@common/crud/frontend-shared';
import { FormServiceSaveHandlerEditableApiItem, FsOnAfterSaveEvent } from '@common/forms/frontend-shared';
import { DestroyRef, inject } from '@angular/core';
import { getCurrentRouteState, takeWhileAlive } from '@core/frontend-shared';
import { convertToBehaviorSubject } from '@core/shared/utils';
import * as i0 from "@angular/core";
import * as i1 from "../crud-manager";
import * as i2 from "@common/crud/frontend-shared";
// NOTE: provided in CrudManager2Component
export let CrudItemViewServiceCrudManager = /*#__PURE__*/(() => {
  class CrudItemViewServiceCrudManager {
    constructor(crudManager, crudFormServiceFactory) {
      this.crudManager = crudManager;
      this.crudFormServiceFactory = crudFormServiceFactory;
      this.destroyRef = inject(DestroyRef);
      this.itemEditorDetails$ = convertToBehaviorSubject(null)(this.modeAndItem$.pipe(pairwise(), map(([previous, current]) => {
        const details = this._getItemEditorDetails(current.mode, current.item);
        return details;
      }), takeWhileAlive(this.destroyRef)));
      this.fsSubscription = this.itemEditorDetails$.pipe(map(details => details?.formService), filter(fs => !!fs), switchMap(fs => {
        return fs.events$.pipe(filter(e => e instanceof FsOnAfterSaveEvent), map(event => {
          return {
            event,
            fs
          };
        }));
      }), takeWhileAlive(this.destroyRef)).subscribe(({
        event,
        fs
      }) => {
        if (!event.error) {
          this.onAfterSave(fs, event.item);
        }
      });
    }
    get itemEditorDetails() {
      return this.itemEditorDetails$.getValue();
    }
    get modeAndItem$() {
      return this.crudManager.modeAndItem$;
    }
    get contextGroups$() {
      return this.modeAndItem$.pipe(map(({
        mode
      }) => {
        return CrudManagerUtils.getContextGroups(mode);
      }), takeWhileAlive(this.destroyRef));
    }
    get crudService() {
      return this.crudManager.crudService;
    }
    get Model() {
      return this.crudManager.Model$.getValue();
    }
    get editorConfig() {
      return this.crudManager.config.editor;
    }
    get enableEdit() {
      return this.crudManager.config.list.editable;
    }
    setPageTitle(title) {
      this.crudManager.setPageTitle(title);
    }
    getPageTitle() {
      return this.crudManager.config.pageTitle;
    }
    navigateTo(requestedView, param) {
      this.crudManager.navigateTo(requestedView, param);
    }
    reload(onlyCurrentItem) {
      this.crudManager.reload(onlyCurrentItem);
    }
    getInnerFormTemplate() {
      return this.crudManager.templates[CrudTemplate.EditorInnerForm];
    }
    getItemActions$(forSpecificMode) {
      return this.crudManager.getItemActions$(forSpecificMode);
    }
    onAfterSave(fs, item) {
      const fsEditMode = fs.getEditMode();
      // TODO: could be replaced by more elegant crudManager.navigateTo(...)
      if (this.crudManager.config.editor.closeOnSave) {
        this.crudManager.navigateTo('list');
      } else if (fsEditMode === 'new' || fsEditMode === 'clone') {
        if (this.crudManager.enableEdit) {
          const id = item.id;
          if (typeof id !== 'number') {
            console.warn('CrudEditor FsOnAfterSaveEvent subscriber received item without a valid ID. Aborting navigation to item edit view.', id, item);
            return;
          }
          this.crudManager.navigateTo('edit', id);
          // this.router.navigate(['../',id],{relativeTo:this.route})
        } else {
          this.crudManager.navigateTo('list');
          // this.router.navigate(['../'],{relativeTo:this.route})
        }
      } else if (fsEditMode === 'batch') {
        this.crudManager.navigateTo('list');
        // this.router.navigate(['../'],{relativeTo:this.route})
      } else {
        // we stay in the editor view. 
        // NOTE: 
        // there are multiple hooks interacting with form reload/re-patching persisted form value:
        // - reload will be triggered via CrudRequestState -> CrudItemViewComponent subscriber
        // - formServiceSaveHandlers with returnsUpdatedFormData set to true will trigger a reload right inside formService
        // - this function will be called as well when the formService emits its onAfterSave event
        // as a result, CrudRequestState is not notified anymore. 
        // but it doesn't seem to cause issues, the FormService value update via save handler seems to be sufficient.
        // In case an edge case is discovered which is not working well anymore, it must be documented.
        // + Implementation must take care not to introduce duplicate reloads.
        // this.crudManager.reload(true)
      }
    }
    _getItemEditorDetails(/*previousMode:CrudManagerMode,*/mode, initialItem) {
      const currFormService = this.itemEditorDetails?.formService;
      const canReuseFormService = /*previousMode === mode &&*/currFormService && currFormService.getEditMode() === mode;
      if (mode === 'list') return null;
      if (mode === 'view') {
        return {
          formService: null,
          instructions: null,
          mode,
          initialItem
        };
      }
      if (mode === 'batch') {
        // if(this.batchFormService) {
        // 	this.batchFormService.resetForm();
        // } else {
        // 	this.batchFormService = this.buildBatchFormService();;
        // }
        // this.setPageTitle('Stapelverarbeitung');
        const crudInstructions = this.crudManager.config.batch.instructions;
        const instructions = Array.isArray(crudInstructions) ? crudInstructions : crudInstructions[mode];
        const formService = this.buildBatchFormService();
        return {
          formService,
          instructions,
          mode,
          initialItem
        };
      } else {
        // for all item-based modes clone, edit, new
        if (!initialItem) return null;
        const defaultItemData = this.createDefaultItemData(mode, initialItem);
        const crudInstructions = this.crudManager.config.editor.instructions;
        const instructions = Array.isArray(crudInstructions) ? crudInstructions : crudInstructions[mode];
        let formService;
        if (canReuseFormService) {
          formService = this.itemEditorDetails.formService;
          // decided not to call .resetForm(). instead selected a more specific approach:
          // we want the form to reflect the persisted value and its state to be reset to initial values.
          // so we call exactly those methods instead of brute-forcing full reset.
          formService.setValue(defaultItemData);
          formService.resetFormState();
        } else {
          formService = this.createFormService(defaultItemData, mode);
        }
        if (!IS_PRODUCTION && mode !== formService.getEditMode()) throw new Error('_getItemEditorDetails mode and FormService mode mismatch: ' + mode + ' ' + formService.getEditMode());
        return {
          formService,
          instructions,
          mode,
          initialItem
        };
      }
    }
    createDefaultItemData(mode, item) {
      const state = getCurrentRouteState();
      if (state[CRUD_ROUTE_STATE_ITEM_DEFAULTS]) {
        if (mode === 'edit' || mode === 'batch') {
          throw new Error('Passing default item values is not supported for ItemEditMode ' + mode);
        }
        item = {
          ...item,
          ...state[CRUD_ROUTE_STATE_ITEM_DEFAULTS]
        };
      }
      return item;
    }
    createFormService(item = null, mode = 'edit') {
      let formService = this.crudFormServiceFactory.useDestroyRef(this.destroyRef).createCrudFormService(this.Model);
      const editorConfig = this.crudManager.config.editor;
      formService.setEditMode(mode);
      formService.enableAutoSave(editorConfig.autoSave);
      if (item) formService.setInitialValue(item);
      const def = typeof editorConfig.editorDefinition === 'object' ? editorConfig.editorDefinition[mode] : editorConfig.editorDefinition;
      formService.setEditorDefinition(def || DEFAULT_EDITOR_DEFINITION_ID);
      const customizeCb = this.crudManager.modifiers.formService;
      if (customizeCb) formService = customizeCb(formService, item);
      formService.setSaveHandler(FormServiceSaveHandlerEditableApiItem, h => h.setService(this.crudManager.service.crud));
      formService.initialize();
      return formService;
    }
    buildBatchFormService() {
      const batchConfig = this.crudManager.config.batch;
      if (!batchConfig) throw new Error('Batch mode is not configurated!');
      if (!batchConfig.batchModel) throw new Error('Batch configuration misses Model to use');
      if (!batchConfig.transformFunction) throw new Error('Batch configuration misses transformFunction');
      if (!batchConfig.instructions) throw new Error('Batch configuration misses instructions');
      if (!batchConfig.targetCrudEndpoint) throw new Error('Batch configuration misses targetCrudEndpoint');
      const BatchModel = batchConfig.batchModel;
      const batchFormService = this.crudFormServiceFactory.useDestroyRef(this.destroyRef).createModelForm(BatchModel);
      batchFormService.setEditMode('batch');
      batchFormService.setEditorDefinition(batchConfig.editorDefinition || 'default');
      const Handler = makeFormServiceSaveHandlerApiCrudBatchCreate(batchConfig.transformFunction, this.Model, batchConfig.targetCrudEndpoint, this.crudManager.service.crud);
      batchFormService.setSaveHandler(Handler);
      batchFormService.initialize();
      return batchFormService;
    }
    static {
      this.ɵfac = function CrudItemViewServiceCrudManager_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CrudItemViewServiceCrudManager)(i0.ɵɵinject(i1.CrudManagerService), i0.ɵɵinject(i2.CrudFormServiceFactory));
      };
    }
    static {
      this.ɵprov = /*@__PURE__*/i0.ɵɵdefineInjectable({
        token: CrudItemViewServiceCrudManager,
        factory: CrudItemViewServiceCrudManager.ɵfac
      });
    }
  }
  return CrudItemViewServiceCrudManager;
})();